// Kalkylekalkulator (/hjelpemidler), i startsidens stil (planer/ny-kundesamhandling-plan.md).
// Regner ut kalkyle og dekningsbidrag på en vare: hent den fra varelisten eller skriv inn
// innkjøpspris og veiledende pris, og velg rabatt eller ønsket kalkyle. Varelisten er
// produktene fra /produkter (ProduktSok.jsx).
//
// Regnestykket er det samme som før:
//   pris til kunden = veiledende pris − rabatt (avrundet)
//   pris eks. mva   = pris til kunden × 0,8 (25 % mva)
//   DB kroner       = pris eks. mva − innkjøpspris
//   salgskalkyle    = DB kroner / pris eks. mva
//   husleie         = pris eks. mva × husleieprosent (Røyken 5 %, Slemmestad 6,5 %)
// Ønsket kalkyle gir rabatten: pris = innkjøpspris / (1 − kalkyle) × 1,25.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import ProduktSok from './components/ProduktSok';
import { FARGER, FORSIDE } from './ny-kundesamhandling/handlinger';
import Ikon from './ny-kundesamhandling/Ikon';

const BUTIKKER = [
  { id: 'Røyken', navn: 'Sport 1 Røyken', leie: 5 },
  { id: 'Slemmestad', navn: 'Sport 1 Slemmestad', leie: 6.5 },
];

// Godtar «1 234,50», «1234.5» og «1 234 kr». Tomt eller ugyldig gir null.
function tall(v) {
  const s = String(v ?? '').replace(/\s|kr|%/gi, '').replace(',', '.');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

const kr = (n) => `${new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 }).format(n)} kr`;
const prosent = (n) => `${new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n)} %`;

function regnUt({ kost, pris, rabatt, leie }) {
  if (kost == null || pris == null || pris <= 0) return null;
  const etterRabatt = Math.round(pris * (1 - (rabatt || 0) / 100));
  const exMva = etterRabatt * 0.8;
  const dbKr = Math.round(exMva - kost);
  const dbProsent = exMva > 0 ? (dbKr / exMva) * 100 : null;
  const husleie = Math.round(exMva * (leie / 100));
  return { etterRabatt, dbKr, dbProsent, husleie, dbEtterHusleie: dbKr - husleie };
}

// Rabatten som gir ønsket kalkyle på veiledende pris.
function rabattForKalkyle({ kost, pris, kalkyle }) {
  if (kost == null || pris == null || pris <= 0 || kalkyle == null || kalkyle >= 100) return null;
  const kalkylePris = Math.round((kost / (1 - kalkyle / 100)) * 1.25);
  return ((pris - kalkylePris) / pris) * 100;
}

function Felt({ id, etikett, hjelp, enhet, value, onChange, ugyldig }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block font-semibold text-slate-800">{etikett}</label>
      <div className="relative">
        <input
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={ugyldig || undefined}
          aria-describedby={hjelp || ugyldig ? `${id}-hjelp` : undefined}
          className={`w-full rounded-xl border-2 bg-white py-1.5 pl-4 pr-12 text-base focus:outline-none focus:ring-2 focus:ring-indigo-600/30 ${
            ugyldig ? 'border-red-600 focus:border-red-600' : 'border-slate-300 focus:border-indigo-600'
          }`}
        />
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 font-medium text-slate-600">{enhet}</span>
      </div>
      {(hjelp || ugyldig) && (
        <p id={`${id}-hjelp`} className={`mt-1 text-sm ${ugyldig ? 'font-medium text-red-700' : 'text-slate-600'}`}>
          {ugyldig ? 'Skriv et tall, for eksempel 1 249,50.' : hjelp}
        </p>
      )}
    </div>
  );
}

function Tall({ etikett, verdi, stor, tone, className = '' }) {
  const farge = tone === 'negativ' ? 'text-red-700' : tone === 'positiv' ? 'text-emerald-800' : 'text-slate-900';
  return (
    <div className={`min-w-0 rounded-xl bg-white p-2.5 sm:p-3 ${stor ? 'ring-2 ring-emerald-500' : 'ring-1 ring-slate-200'} ${className}`}>
      <dt className="text-sm text-slate-600">{etikett}</dt>
      <dd className={`font-bold ${stor ? 'text-2xl' : 'text-lg'} ${farge}`}>{verdi}</dd>
    </div>
  );
}

function Calculator() {
  const [kostPris, setKostPris] = useState('');
  const [salgsPris, setSalgsPris] = useState('');
  const [rabatt, setRabatt] = useState('');
  const [kalkyleProsent, setKalkyleProsent] = useState('');
  const [vare, setVare] = useState(null);
  // Butikken den ansatte er logget inn i (butikkid 1 = Røyken, 2 = Slemmestad).
  const [butikk, setButikk] = useState(() => (Cookies.get('butikkid') === '1' ? 'Røyken' : 'Slemmestad'));

  const leie = BUTIKKER.find((b) => b.id === butikk).leie;
  const kost = tall(kostPris);
  const pris = tall(salgsPris);
  const kalkyle = tall(kalkyleProsent);
  // Ønsket kalkyle styrer rabatten; endres rabatten for hånd, tømmes kalkylefeltet.
  const beregnetRabatt = kalkyleProsent.trim() ? rabattForKalkyle({ kost, pris, kalkyle }) : null;
  const rabattTall = beregnetRabatt != null ? beregnetRabatt : tall(rabatt);
  const rabattVisning = beregnetRabatt != null ? beregnetRabatt.toFixed(1).replace('.', ',') : rabatt;
  const resultat = regnUt({ kost, pris, rabatt: rabattTall, leie });

  const ugyldig = (v) => v.trim() !== '' && tall(v) == null;

  function velgVare({ navn, variant, storrelse }) {
    setKostPris(variant.kostpris != null ? String(variant.kostpris).replace('.', ',') : '');
    setSalgsPris(variant.pris != null ? String(variant.pris).replace('.', ',') : '');
    setRabatt('');
    setKalkyleProsent('');
    setVare({ navn, variant, storrelse });
  }

  function nullstill() {
    setKostPris('');
    setSalgsPris('');
    setRabatt('');
    setKalkyleProsent('');
    setVare(null);
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <Link to={FORSIDE} className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 font-medium text-indigo-700 hover:bg-indigo-50">
        <Ikon navn="tilbake" className="w-5 h-5" /> Tilbake til kontrollpanelet
      </Link>

      <header className="flex items-center gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${FARGER.betaling.ikon}`}>
          <Ikon navn="kalkulator" className="w-6 h-6" />
        </span>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 text-balance">Kalkylekalkulator</h1>
          <p className="text-slate-600">Regn ut kalkyle og dekningsbidrag på en vare.</p>
        </div>
      </header>

      <section aria-labelledby="kk-vare" className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-3">
        <h2 id="kk-vare" className="text-base font-bold text-slate-900">Finn varen</h2>
        <ProduktSok id="kk-produkt" onVelg={velgVare} />
        {vare && (
          <div role="status" className="space-y-1 rounded-xl border border-emerald-200 bg-white p-2.5 text-slate-800">
            <p className="flex flex-wrap items-center gap-2">
              <Ikon navn="hake" className="w-5 h-5 shrink-0 text-emerald-700" />
              <span className="min-w-0">
                Hentet <strong className="font-semibold [overflow-wrap:anywhere]">{vare.navn}</strong>
                {vare.storrelse && <> i størrelse <strong className="font-semibold">{vare.storrelse}</strong></>}.
              </span>
            </p>
            {(vare.variant.kostpris == null || vare.variant.pris == null) && (
              <p className="font-medium text-amber-800">
                Varen mangler {vare.variant.kostpris == null && vare.variant.pris == null ? 'kostpris og pris' : vare.variant.kostpris == null ? 'kostpris' : 'pris'} i produktlisten. Fyll inn under.
              </p>
            )}
            {vare.variant.tilbudspris != null && (
              <p className="text-sm text-slate-700">Tilbudspris i nettbutikken: {kr(vare.variant.tilbudspris)}.</p>
            )}
          </div>
        )}
      </section>

      <section aria-labelledby="kk-priser" className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 id="kk-priser" className="text-base font-bold text-slate-900">Priser</h2>
          {(kostPris || salgsPris || rabatt || kalkyleProsent) && (
            <button
              type="button"
              onClick={nullstill}
              className="ml-auto inline-flex min-h-[2.25rem] items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
            >
              <Ikon navn="gjenta" className="w-5 h-5" /> Nullstill
            </button>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Felt id="kk-kost" etikett="Innkjøpspris" hjelp="Uten mva" enhet="kr" value={kostPris} onChange={setKostPris} ugyldig={ugyldig(kostPris)} />
          <Felt id="kk-pris" etikett="Veiledende pris" hjelp="Med mva" enhet="kr" value={salgsPris} onChange={setSalgsPris} ugyldig={ugyldig(salgsPris)} />
          <Felt
            id="kk-rabatt"
            etikett="Rabatt"
            hjelp={beregnetRabatt != null ? 'Regnet ut fra ønsket kalkyle' : 'Rabatt på veiledende pris'}
            enhet="%"
            value={rabattVisning}
            onChange={(v) => { setRabatt(v); setKalkyleProsent(''); }}
            ugyldig={beregnetRabatt == null && ugyldig(rabatt)}
          />
          <Felt
            id="kk-kalkyle"
            etikett="Ønsket salgskalkyle"
            hjelp="Fyll inn for å regne ut rabatten"
            enhet="%"
            value={kalkyleProsent}
            onChange={setKalkyleProsent}
            ugyldig={ugyldig(kalkyleProsent)}
          />
        </div>

        <div role="group" aria-labelledby="kk-butikk" className="space-y-1.5">
          <span id="kk-butikk" className="block font-semibold text-slate-800">Butikk</span>
          <div className="flex flex-wrap gap-2">
            {BUTIKKER.map((b) => (
              <button
                key={b.id}
                type="button"
                aria-pressed={butikk === b.id}
                onClick={() => setButikk(b.id)}
                className={`min-h-[2.25rem] rounded-xl border-2 px-3 py-1.5 text-left font-semibold transition touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${
                  butikk === b.id ? 'border-indigo-700 bg-indigo-700 text-white' : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
                }`}
              >
                {b.navn} <span className="font-normal">(husleie {String(b.leie).replace('.', ',')} %)</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="kk-resultat" className="space-y-2 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-3">
        <h2 id="kk-resultat" className="text-base font-bold text-slate-900">
          Kalkyle for {BUTIKKER.find((b) => b.id === butikk).navn}
        </h2>
        <div aria-live="polite">
          {!resultat ? (
            <p className="text-slate-700">Fyll inn innkjøpspris og veiledende pris, så kommer kalkylen her.</p>
          ) : (
            <dl className="grid grid-cols-2 gap-2 lg:grid-cols-3">
              <Tall
                stor
                className="col-span-2 lg:col-span-1"
                etikett="Salgskalkyle"
                verdi={resultat.dbProsent != null ? prosent(resultat.dbProsent) : '–'}
                tone={resultat.dbProsent != null && resultat.dbProsent < 0 ? 'negativ' : 'positiv'}
              />
              <Tall etikett="Pris til kunden" verdi={kr(resultat.etterRabatt)} />
              <Tall etikett="DB kroner" verdi={kr(resultat.dbKr)} tone={resultat.dbKr < 0 ? 'negativ' : undefined} />
              <Tall etikett={`Husleie (${String(leie).replace('.', ',')} %)`} verdi={kr(resultat.husleie)} />
              <Tall
                etikett="DB etter husleie"
                verdi={kr(resultat.dbEtterHusleie)}
                tone={resultat.dbEtterHusleie < 0 ? 'negativ' : 'positiv'}
              />
            </dl>
          )}
        </div>
      </section>
    </div>
  );
}

export default Calculator;
